import React, { FC, useContext, useEffect, useRef, useState } from 'react';

import { useLazyQuery } from '@apollo/client';
import {
  Button,
  ContextMenu,
  ITabOption,
  NumberInput,
  Pagination,
  Preloader,
  Table,
  Tabs,
  useContextMenu,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { LayoutContext } from 'components/Layout';
import { LocalizationContext } from 'localization';
import {
  COMPANY_NEWS_LIST,
  ICompanyNewsList,
  ICompanyNewsListRequest,
  ICompanyNewsListResponse,
} from 'schemas';

import { FeatureHOC } from '../../features';
import { useColumns, useContextMenuItems } from './hooks';
import { ETabs } from './interfaces';
import s from './News.module.scss';

export const News: FC = () => {
  const { t } = useContext(LocalizationContext);
  const navigate = useNavigate();
  const layoutElement = useContext(LayoutContext);

  const [tab, setTab] = useState<ETabs>(ETabs.PUBLISHED);
  const [pagesInfo, setPagesInfo] = useState({ currentPage: 1, lastPage: 1 });
  const { lastPage, currentPage } = pagesInfo;
  const TAB_OPTIONS: Array<ITabOption<ETabs>> = [
    { title: t('news.published'), value: ETabs.PUBLISHED },
    { title: t('news.unpublished'), value: ETabs.UNPUBLISHED },
  ];
  const [isPaginationVisible, setIsPaginationVisible] = useState(false);

  const { control, handleSubmit } = useForm({});

  const [getCompanyNewsList, { data: news, loading }] = useLazyQuery<
    ICompanyNewsListResponse,
    ICompanyNewsListRequest
  >(COMPANY_NEWS_LIST, {
    fetchPolicy: 'no-cache',
    onCompleted: ({ companyNewsList }) => {
      if (!companyNewsList) return;

      setPagesInfo((prev) => ({
        ...prev,
        lastPage: companyNewsList.paginatorInfo.lastPage,
        currentPage: companyNewsList.paginatorInfo.currentPage,
      }));
    },
  });

  useEffect(() => {
    getCompanyNewsList({
      variables: {
        input: { active: true },
        page: 1,
        first: 25,
      },
    });
  }, []);

  useEffect(() => {
    getCompanyNewsList({
      variables: {
        input: { active: tab === ETabs.PUBLISHED },
        page: 1,
        first: 25,
      },
    });
    setPagesInfo((prev) => ({ ...prev, currentPage: 1 }));
  }, [tab]);

  const handlePageChange = (page: number): void => {
    if (page !== currentPage) {
      setPagesInfo((prev) => ({ ...prev, currentPage: page }));

      getCompanyNewsList({
        variables: {
          input: { active: tab === ETabs.PUBLISHED },
          page,
          first: 25,
        },
      });
    }
  };

  const {
    contextMenuData,
    closeContextMenu,
    onContextMenu,
    cursorPosition,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useContextMenu<ICompanyNewsList>();
  const contextMenuItems = useContextMenuItems();

  const currentNews = news?.companyNewsList?.data || [];
  const columns = useColumns();
  const paginationTopRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollEl = document.querySelector('main');
    const handleScroll = (): (() => void) | undefined => {
      if (!paginationTopRef.current) return;

      const { bottom } = paginationTopRef.current.getBoundingClientRect();
      const isOutOfView = bottom < 70;
      setIsPaginationVisible(isOutOfView);
    };

    scrollEl?.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => scrollEl?.removeEventListener('scroll', handleScroll);
  }, [paginationTopRef]);

  return (
    <FeatureHOC alias="club_news" strategy="fade">
      <div className={s.container} ref={containerRef}>
        <div className={s.header} ref={paginationTopRef}>
          <Tabs
            className={s.tabs}
            options={TAB_OPTIONS}
            onChange={setTab}
            value={tab}
          />

          <div className={s.left}>
            <Button
              onClick={() => {
                navigate('/news/new', { replace: true });
              }}
              iconName="CirclePlusIcon"
              className={s.add}
            >
              {t('news.create')}
            </Button>
            {lastPage > 1 && (
              <div
                className={cn(
                  s.pagination_bottom,
                  isPaginationVisible && s.visible
                )}
              >
                <Pagination
                  onChange={(page: number) => {
                    if (page != pagesInfo.currentPage) {
                      handlePageChange(page);
                    }
                  }}
                  lastPage={pagesInfo.lastPage}
                  currentPage={pagesInfo.currentPage}
                  className={s.pagination}
                  isChose={true}
                />
                {pagesInfo && (
                  <form
                    onSubmit={handleSubmit(({ page }) =>
                      handlePageChange(page || 1)
                    )}
                  >
                    <Controller
                      name="page"
                      control={control}
                      render={({ field: { onChange } }) => (
                        <NumberInput
                          value={pagesInfo.currentPage}
                          onChange={(val) => {
                            if (typeof val === 'number') {
                              if (val < 1) {
                                onChange(1);
                              } else if (val > pagesInfo?.lastPage) {
                                onChange(pagesInfo?.lastPage);
                              } else {
                                onChange(val);
                              }
                            } else {
                              onChange('');
                            }
                          }}
                          variant="secondary"
                          placeholder="№"
                          className={s.pagination_input}
                        />
                      )}
                    />
                    <button type="submit" style={{ display: 'none' }} />
                  </form>
                )}
              </div>
            )}
          </div>
        </div>

        <Preloader loading={currentNews?.length === 0 && loading}>
          <div
            className={cn(s.main, currentNews?.length === 0 && s.main_empty)}
          >
            {contextMenuData && cursorPosition && (
              <ContextMenu
                layoutElement={layoutElement}
                data={contextMenuData}
                items={contextMenuItems}
                closeContextMenu={closeContextMenu}
                position={cursorPosition}
              />
            )}
            {currentNews?.length > 0 ? (
              <Table
                onContextMenu={onContextMenu}
                onTouchStart={(event, row) => {
                  onTouchStart(event, row);
                }}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                isLoading={loading}
                data={currentNews}
                columns={columns}
                className={s.table}
                onRowDoubleClick={(event, row) => {
                  navigate(`/news/${row.id}`);
                }}
              />
            ) : (
              <span className={s.empty}>
                {tab === ETabs.PUBLISHED
                  ? t('empty.published')
                  : t('empty.unpublished')}
              </span>
            )}
          </div>
        </Preloader>
      </div>
    </FeatureHOC>
  );
};
